import { useEffect, useState } from "react";
import { Typography, Flex, Table, Select, DatePicker, Tag, Avatar, Button, Tooltip, Modal, Checkbox } from "antd";
import { HomeOutlined, ReloadOutlined, SettingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useTenantNavigation } from "../../../hooks/useTenantNavigation";
import BreadCrumb from "../../BreadCrumb";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { saveActivityConfig, getActivityConfig, getActivityLogs, getActivityFilters } from "../../../services/adminServices";
import { showLoader } from "../../../utils/showloader";
import { useSelector } from "react-redux";
import UserRoleDropdown from "../../UserRoleDropdown";
import Paginator from "../../space/components/table/Paginator";

dayjs.extend(relativeTime);

const { Title } = Typography;
const { RangePicker } = DatePicker;

const MODULE_OPTIONS = [
    { label: "Space", value: "SPACE", actions: ['CREATE', 'UPDATE', 'DELETE'] },
    { label: "Workflow", value: "WORKFLOW", actions: ['CREATE', 'UPDATE', 'DELETE'] },
    { label: "Workflow Condition", value: "WORKFLOW_CONDITION", actions: ['CREATE', 'UPDATE', 'DELETE'] },
    { label: "Form", value: "FORM", actions: ['CREATE', 'UPDATE', 'DELETE'] },
    { label: "Form Condition", value: "FORM_CONDITION", actions: ['CREATE', 'UPDATE', 'DELETE'] },
    { label: "User Management", value: "USER", actions: ['CREATE', 'UPDATE', 'DELETE'] },
    { label: "Role Management", value: "ROLE", actions: ['CREATE', 'UPDATE', 'DELETE'] },
    { label: "Group Management", value: "GROUP", actions: ['CREATE', 'UPDATE', 'DELETE'] },
    { label: "Master Data", value: "MASTER_DATA", actions: ['CREATE', 'UPDATE', 'DELETE'] },
    { label: "Notification", value: "NOTIFICATION", actions: ['CREATE', 'UPDATE'] },
    { label: "Request", value: "REQUEST", actions: ['CREATE', 'UPDATE', 'DELETE', 'EXPORT'] },
    { label: "Login", value: "LOGIN", actions: ['LOGIN'] }
];

const ACTION_COLOR_MAP = {
    create: "green",
    update: "blue",
    delete: "red",
    export: "cyan",
    import: "geekblue",
};

const ACTION_OPTIONS = [
    { label: "Create", value: "CREATE" },
    { label: "Update", value: "UPDATE" },
    { label: "Delete", value: "DELETE" },
    { label: "Export", value: "EXPORT" },
    { label: "Import", value: "IMPORT" },
    { label: "Login", value: "LOGIN" },
];

const ActivityMonitoring = () => {
    const { t } = useTranslation();
    const navigate = useTenantNavigation();
    const { userInfo } = useSelector(state => state.user);

    // Filters
    const [selectedAction, setSelectedAction] = useState(null);
    const [selectedUserOptions, setSelectedUserOptions] = useState({ user: [], role: [], group: [] });
    const [selectedModule, setSelectedModule] = useState(null);
    const [dateRange, setDateRange] = useState(null);

    // Configure modal
    const [configModalOpen, setConfigModalOpen] = useState(false);
    const getActionsForModule = (moduleValue) =>
        MODULE_OPTIONS.find(m => m.value === moduleValue)?.actions || [];
    const [moduleConfig, setModuleConfig] = useState(() => {
        const initial = {};
        MODULE_OPTIONS.forEach(mod => {
            initial[mod.value] = { enabled: false, activities: [] };
        });
        return initial;
    });
    const [tempConfig, setTempConfig] = useState(moduleConfig);
    const [activityLogs, setActivityLogs] = useState([]);

    // Pagination
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [appliedFilters, setAppliedFilters] = useState(null);

    const parseLogsResponse = (response) => {
        const result = response?.data?.result;
        let logs = [];
        let total = 0;
        if (Array.isArray(result)) {
            logs = result;
            total = response?.data?.totalCount ?? response?.data?.totalElements ?? result.length;
        } else {
            logs = result?.data ?? result?.content ?? result?.logs ?? [];
            total = result?.totalCount ?? result?.totalElements ?? logs.length;
        }
        return { logs, total };
    };

    const loadLogs = async (currentPage, size, filters) => {
        if (!userInfo?.userId) return;
        showLoader(true);
        let response = filters
            ? await getActivityFilters(filters, userInfo, currentPage, size)
            : await getActivityLogs(userInfo, currentPage, size);
        showLoader(false);
        const { logs, total } = parseLogsResponse(response);
        setActivityLogs(logs);
        setTotalCount(total);
    };

    useEffect(() => {
        loadLogs(page, pageSize, appliedFilters);
    }, [userInfo, page, pageSize, appliedFilters]);

    useEffect(() => {
        const fetchActivityConfig = async () => {
            if (!userInfo?.userId) return;
            let response = await getActivityConfig(userInfo);
            if (response?.data) {
                const configData = Array.isArray(response.data) ? response.data : response.data?.result || [];
                const savedConfig = {};
                MODULE_OPTIONS.forEach(mod => {
                    savedConfig[mod.value] = { enabled: false, activities: [] };
                });
                configData.forEach(item => {
                    if (item.module && savedConfig[item.module] !== undefined) {
                        const enabledActivities = Object.entries(item.action || {})
                            .filter(([, val]) => val)
                            .map(([key]) => key);
                        savedConfig[item.module] = {
                            enabled: enabledActivities.length > 0,
                            activities: enabledActivities,
                        };
                    }
                });
                setModuleConfig(savedConfig);
                setTempConfig(savedConfig);
            }
        };
        fetchActivityConfig();
    }, [userInfo]);

    const handleOpenConfig = () => {
        setTempConfig(JSON.parse(JSON.stringify(moduleConfig)));
        setConfigModalOpen(true);
    };

    const handleSaveConfig = async () => {
        const payload = Object.entries(tempConfig)
            .filter(([, config]) => config.enabled)
            .map(([moduleValue, config]) => {
                const allActivities = getActionsForModule(moduleValue);
                const action = {};
                allActivities.forEach(activity => {
                    action[activity] = config.activities.includes(activity);
                });
                return { module: moduleValue, action };
            });
        showLoader(true)
        let activityResponse = await saveActivityConfig(payload);
        showLoader(false)
        setModuleConfig(tempConfig);
        setConfigModalOpen(false);
    };

    const toggleModule = (moduleValue) => {
        setTempConfig(prev => {
            const current = prev[moduleValue];
            return {
                ...prev,
                [moduleValue]: {
                    ...current,
                    enabled: !current.enabled,
                    activities: !current.enabled ? [...getActionsForModule(moduleValue)] : [],
                },
            };
        });
    };

    const toggleActivity = (moduleValue, activity) => {
        setTempConfig(prev => {
            const current = prev[moduleValue];
            const activities = current.activities.includes(activity)
                ? current.activities.filter(a => a !== activity)
                : [...current.activities, activity];
            return {
                ...prev,
                [moduleValue]: {
                    ...current,
                    enabled: activities.length > 0,
                    activities,
                },
            };
        });
    };

    const handleReset = () => {
        setSelectedAction(null);
        setSelectedUserOptions({ user: [], role: [], group: [] });
        setSelectedModule(null);
        setDateRange(null);
        setPage(1);
        setAppliedFilters(null);
    };

    const handleSearch = () => {
        let searchParams = {
            email: selectedUserOptions.user?.length > 0 ? selectedUserOptions.user[0] : "",
            activity: selectedModule,
            action: selectedAction,
            activityDate: dateRange && dateRange[0] && dateRange[1] ? `${dateRange[0].format("YYYY-MM-DD")}-${dateRange[1].format("YYYY-MM-DD")}` : "",
        };
        setPage(1);
        setAppliedFilters(searchParams);
    }

    const getActionColor = (action) => {
        const key = action?.toLowerCase();
        for (const [keyword, color] of Object.entries(ACTION_COLOR_MAP)) {
            if (key?.includes(keyword)) return color;
        }
        return "default";
    };

    const columns = [
        {
            title: t("User"),
            dataIndex: "userName",
            key: "userName",
            render: (text, record) => (
                <Flex align="center" gap={10}>
                    <Avatar size={32} style={{ backgroundColor: "#141BCB", flexShrink: 0 }}>
                        {(text || record.email || "U").charAt(0).toUpperCase()}
                    </Avatar>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {text || record.email || "Unknown"}
                        </div>
                        {record.email && text && (
                            <div style={{ fontSize: 12, color: "#8c8c8c", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {record.email}
                            </div>
                        )}
                    </div>
                </Flex>
            ),
        },
        {
            title: t("Action"),
            dataIndex: "action",
            key: "action",
            width: 200,
            render: (action) => (
                <Tag color={getActionColor(action)}>
                    {action?.toUpperCase() || "N/A"}
                </Tag>
            ),
        },
        {
            title: t("Activity"),
            dataIndex: "activity",
            key: "activity",
            width: 200,
            render: (activity) => {
                const found = MODULE_OPTIONS.find(m => m.value === activity);
                return found?.label || activity || "—";
            },
        },
        {
            title: t("Details"),
            dataIndex: "details",
            key: "details",
            ellipsis: true,
        },
        {
            title: t("Date & Time"),
            dataIndex: "requestTimestamp",
            key: "requestTimestamp",
            width: 200,
            render: (timestamp) => {
                if (!timestamp) return "—";
                const d = dayjs(Number(timestamp));
                return (
                    <div>
                        <div>{d.format("DD MMM YYYY, hh:mm A")}</div>
                    </div>
                );
            },
        },
    ];

    return (
        <div className="px-10 py-4">
            <BreadCrumb
                items={[
                    { title: <HomeOutlined />, path: "/app/dashboard" },
                    { title: t("Admin"), path: "/app/admin/config" },
                    { title: t("Activity Monitoring") },
                ]}
                marginb="12px"
            />

            <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
                <Title className="text-h2 text-weight-600" style={{ margin: 0 }}>
                    {t("Activity Monitoring")}
                </Title>
            </Flex>

            {/* Filters */}
            <Flex align="center" gap={12} style={{ marginBottom: 20 }}>
                <Flex wrap="wrap" gap={12} align="center" style={{ flex: 1 }}>
                    <Select
                        placeholder={t("Filter by action")}
                        value={selectedAction}
                        onChange={setSelectedAction}
                        options={ACTION_OPTIONS}
                        allowClear
                        style={{ width: 220 }}
                    />
                    <div style={{ width: 240 }}>
                        <UserRoleDropdown
                            placeholder={t("Filter by user")}
                            selectedOptions={selectedUserOptions}
                            onSelect={(newOptions) => setSelectedUserOptions({
                                user: newOptions.user || [],
                                role: newOptions.role || [],
                                group: newOptions.group || [],
                            })}
                            tabsRequired={["user"]}
                            getAllGroups={true}
                        />
                    </div>
                    <Select
                        placeholder={t("Filter by module")}
                        value={selectedModule}
                        onChange={setSelectedModule}
                        options={MODULE_OPTIONS}
                        allowClear
                        style={{ width: 200 }}
                    />
                    <RangePicker
                        value={dateRange}
                        onChange={setDateRange}
                        style={{ width: 280 }}
                    />
                    {(selectedAction || selectedUserOptions.user.length > 0 || selectedModule || dateRange) && (
                        <Button onClick={handleReset}>{t("Reset")}</Button>
                    )}
                    {(() => {
                        const hasFilters = selectedAction || selectedUserOptions.user.length > 0 || selectedModule || dateRange;
                        return (
                            <Button
                                onClick={handleSearch}
                                type={hasFilters ? "primary" : "default"}
                                disabled={!hasFilters}
                            >
                                {t("Apply")}
                            </Button>
                        );
                    })()}
                </Flex>
                <Tooltip title={t("Configure modules to be monitored")}>
                    <Button
                        icon={<SettingOutlined />}
                        onClick={handleOpenConfig}
                        style={{ fontWeight: 500 }}
                    >
                        {t("Module Configuration")}
                    </Button>
                </Tooltip>
            </Flex>

            {/* Activity Table */}
            <Table
                columns={columns}
                dataSource={activityLogs}
                rowKey="_id"
                pagination={false}
                scroll={{ x: 900 }}
                locale={{ emptyText: t("No activity logs found") }}
            />
            {totalCount > 0 && (
                <div className="flex justify-between items-center" style={{ marginTop: 16 }}>
                    <div className="text-[#000000a9]">
                        {t("Showing")} {(page - 1) * pageSize + 1} {t("to")} {Math.min(page * pageSize, totalCount)} {t("of")} {totalCount} {t("records")}
                    </div>
                    <Paginator
                        total={totalCount}
                        currentPage={page}
                        pageSize={pageSize}
                        onChange={(newPage, newPageSize) => {
                            setPage(newPage);
                            setPageSize(newPageSize);
                        }}
                    />
                </div>
            )}

            {/* Configure Modules Modal */}
            <Modal
                title={t("Configure Monitored Modules")}
                open={configModalOpen}
                onCancel={() => setConfigModalOpen(false)}
                width={600}
                footer={null}
            >
                <div style={{ maxHeight: 420, overflowY: "auto" }}>
                    {MODULE_OPTIONS.map((mod) => {
                        const config = tempConfig[mod.value];
                        return (
                            <div
                                key={mod.value}
                                style={{
                                    border: "1px solid #f0f0f0",
                                    borderRadius: 8,
                                    padding: "12px 16px",
                                    marginBottom: 10,
                                }}
                            >
                                <Checkbox
                                    checked={config?.enabled}
                                    onChange={() => toggleModule(mod.value)}
                                    style={{ fontWeight: 500 }}
                                >
                                    {mod.label}
                                </Checkbox>
                                {config?.enabled && (
                                    <Flex gap={16} style={{ marginTop: 8, paddingLeft: 24 }}>
                                        {getActionsForModule(mod.value).map((activity) => (
                                            <Checkbox
                                                key={activity}
                                                checked={config.activities.includes(activity)}
                                                onChange={() => toggleActivity(mod.value, activity)}
                                            >
                                                {activity.charAt(0).toUpperCase() + activity.slice(1)}
                                            </Checkbox>
                                        ))}
                                    </Flex>
                                )}
                            </div>
                        );
                    })}
                </div>
                <Flex justify="flex-end" gap={8} style={{ marginTop: 16 }}>
                    <Button onClick={() => setConfigModalOpen(false)}>{t("Cancel")}</Button>
                    <Button type="primary" onClick={handleSaveConfig}>{t("Submit")}</Button>
                </Flex>
            </Modal>
        </div>
    );
};

export default ActivityMonitoring;
